"use client";

import {
  forwardRef, useCallback, useEffect, useId, useMemo, useRef, useState,
  type KeyboardEvent, type ReactNode,
} from "react";
import { createPortal } from "react-dom";
import { Check, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/cn";
import { useEscapeStack } from "./overlayStack";
import { useAnchoredPosition } from "./floating";
import { useIsClient } from "./hooks";
import type { InputSize } from "./Input";

/* Phase 4.2 — the searchable Combobox, replacing MultiSelect.
 *
 * MultiSelect had zero call sites, and the reason is visible in its markup:
 * it put each selected chip's remove <button> INSIDE the trigger <button>.
 * Nested interactive elements are invalid HTML, and browsers resolve them by
 * firing both — so removing a chip also toggled the menu. It also had no
 * type-ahead at all, which makes it unusable for the lists this app actually
 * has: hosts, rules, users, tenants, MITRE techniques, tags. A native
 * <select> is fine for six options and hopeless for six hundred.
 *
 * Structure here avoids that class of bug by construction: the trigger is a
 * real <input> (so typing is the primary interaction, not a special mode),
 * the chips sit BESIDE it inside a presentational shell, and the listbox is
 * portalled so it is never clipped by a toolbar or table wrapper.
 *
 * Accessibility follows the ARIA 1.2 combobox pattern rather than being
 * approximated: the input owns `role="combobox"`, `aria-expanded`,
 * `aria-controls` and `aria-activedescendant`; the panel is a real
 * `role="listbox"` of `role="option"` elements. Focus never leaves the
 * input, which is what lets type-ahead and arrow-key navigation coexist —
 * highlighting is communicated through aria-activedescendant, not by moving
 * the focus ring around.
 */

export interface ComboboxOption {
  value: string;
  label: string;
  /** Secondary line — a host's IP, a rule's id, a user's email. */
  hint?: string;
  disabled?: boolean;
}

interface CommonProps {
  options: ReadonlyArray<ComboboxOption>;
  label: ReactNode;
  hideLabel?: boolean;
  placeholder?: string;
  help?: ReactNode;
  error?: ReactNode;
  required?: boolean;
  disabled?: boolean;
  size?: InputSize;
  className?: string;
  id?: string;
  /** Message shown when the filter matches nothing. */
  emptyText?: string;
  /** Caps the rendered list. Lists longer than this filter down to it. */
  maxVisible?: number;
  /** Controls the typed query, for options fetched from a server as the user types. */
  inputValue?: string;
  onInputChange?: (query: string) => void;
  /** Replaces the built-in label/value/hint match; `() => true` when the server already filtered. */
  filter?: (option: ComboboxOption, query: string) => boolean;
}

export interface ComboboxSingleProps extends CommonProps {
  multiple?: false;
  value: string | null;
  onChange: (next: string | null) => void;
}

export interface ComboboxMultiProps extends CommonProps {
  multiple: true;
  value: string[];
  onChange: (next: string[]) => void;
}

export type ComboboxProps = ComboboxSingleProps | ComboboxMultiProps;

function normalise(s: string): string {
  return s.toLowerCase().normalize("NFKD");
}

export const Combobox = forwardRef<HTMLInputElement, ComboboxProps>(function Combobox(props, ref) {
  const {
    options, label, hideLabel, placeholder, help, error, required, disabled,
    size = "md", className, id: providedId, emptyText = "No matches", maxVisible = 100,
  } = props;
  const multiple = props.multiple === true;

  const fallbackId = useId();
  const id = providedId ?? fallbackId;
  const listboxId = `${id}-listbox`;
  const helpId = help ? `${id}-help` : undefined;
  const errorId = error ? `${id}-err` : undefined;

  const [open, setOpen] = useState(false);
  const [ownQuery, setOwnQuery] = useState("");
  const { inputValue, onInputChange, filter } = props;
  const query = inputValue ?? ownQuery;
  const setQuery = useCallback(
    (next: string) => {
      if (inputValue === undefined) setOwnQuery(next);
      onInputChange?.(next);
    },
    [inputValue, onInputChange]
  );
  const [activeRaw, setActive] = useState(0);
  const mounted = useIsClient();

  const shellRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const panelRef = useRef<HTMLDivElement | null>(null);

  const setInputRef = useCallback((node: HTMLInputElement | null) => {
    inputRef.current = node;
    if (typeof ref === "function") ref(node);
    else if (ref) (ref as React.MutableRefObject<HTMLInputElement | null>).current = node;
  }, [ref]);

  const selectedValues = useMemo<string[]>(
    () => (multiple ? (props.value as string[]) ?? [] : props.value ? [props.value as string] : []),
    [multiple, props.value]
  );

  const byValue = useMemo(() => new Map(options.map((o) => [o.value, o])), [options]);

  const filtered = useMemo(() => {
    const q = normalise(query.trim());
    const pool = filter
      ? options.filter((o) => filter(o, query))
      : q
      ? options.filter((o) => normalise(o.label).includes(q) || normalise(o.value).includes(q) || (o.hint ? normalise(o.hint).includes(q) : false))
      : options;
    return pool.slice(0, maxVisible);
  }, [options, query, maxVisible, filter]);

  // An open list is the top overlay: Escape closes it and nothing behind it.
  // The shared stack listens in the capture phase, so without an entry here
  // Escape in a combobox inside a Modal or Drawer closed the dialog, and the
  // input's own handler below never got the chance.
  useEscapeStack(open, () => setOpen(false));

  /* The highlight must stay inside the list as it filters down, or Enter
     commits whatever happens to be at a stale index — type three more
     characters and the row under the cursor is a different option.
     Clamped during render rather than corrected in an effect: an effect
     would render one frame with an out-of-range index, and a keystroke
     landing in that frame would commit the wrong option. */
  const active = filtered.length === 0 ? 0 : Math.min(activeRaw, filtered.length - 1);

  const pos = useAnchoredPosition({ open, anchorRef: shellRef, panelRef, side: "bottom", align: "start", offset: 4, matchTriggerWidth: true });

  const commit = useCallback((option: ComboboxOption) => {
    if (option.disabled) return;
    if (multiple) {
      const current = (props.value as string[]) ?? [];
      const next = current.includes(option.value)
        ? current.filter((v) => v !== option.value)
        : [...current, option.value];
      (props.onChange as (n: string[]) => void)(next);
      // The menu stays open for multi-select: picking three tags should not
      // cost three separate opens. Clearing the query is what makes the next
      // pick immediate.
      setQuery("");
    } else {
      (props.onChange as (n: string | null) => void)(option.value);
      setQuery("");
      setOpen(false);
    }
  }, [multiple, props]);

  const removeValue = useCallback((value: string) => {
    if (multiple) {
      const current = (props.value as string[]) ?? [];
      (props.onChange as (n: string[]) => void)(current.filter((v) => v !== value));
    } else {
      (props.onChange as (n: string | null) => void)(null);
    }
  }, [multiple, props]);

  function onKeyDown(e: KeyboardEvent<HTMLInputElement>) {
    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault();
      if (!open) { setOpen(true); return; }
      setActive((a) => {
        const delta = e.key === "ArrowDown" ? 1 : -1;
        // Wraps, and skips disabled rows rather than parking on one.
        for (let i = 1; i <= filtered.length; i++) {
          const next = (a + delta * i + filtered.length * i) % filtered.length;
          if (!filtered[next]?.disabled) return next;
        }
        return a;
      });
      return;
    }
    if (e.key === "Enter") {
      if (open && filtered[active]) {
        e.preventDefault();
        commit(filtered[active]);
      }
      return;
    }
    if (e.key === "Escape") {
      if (open) {
        // Only swallowed while the menu is open — an Escape on a closed
        // combobox belongs to the dialog around it.
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
      }
      return;
    }
    if (e.key === "Backspace" && !query && multiple && selectedValues.length > 0) {
      // Backspace on an empty query peels off the last chip, the behaviour
      // every tag input has trained people to expect.
      removeValue(selectedValues[selectedValues.length - 1]);
    }
    if (e.key === "Tab" && open) setOpen(false);
  }

  useEffect(() => {
    if (!open) return;
    function onDocMouseDown(e: MouseEvent) {
      const t = e.target as Node;
      if (shellRef.current?.contains(t) || panelRef.current?.contains(t)) return;
      setOpen(false);
      setQuery("");
    }
    document.addEventListener("mousedown", onDocMouseDown);
    return () => document.removeEventListener("mousedown", onDocMouseDown);
  }, [open]);

  // Keeps the highlighted row in view during arrow-key navigation.
  useEffect(() => {
    if (!open) return;
    panelRef.current?.querySelector<HTMLElement>(`[data-index="${active}"]`)?.scrollIntoView({ block: "nearest" });
  }, [active, open]);

  const singleSelected = !multiple && props.value ? byValue.get(props.value as string) : undefined;

  const panel = open && mounted ? createPortal(
    <div
      ref={panelRef}
      id={listboxId}
      role="listbox"
      aria-multiselectable={multiple || undefined}
      aria-label={typeof label === "string" ? label : undefined}
      className="rc-combobox__panel"
      data-open={pos.ready}
      style={{ left: pos.x, top: pos.y, minWidth: pos.minWidth }}
    >
      {filtered.length === 0 ? (
        <div className="rc-combobox__empty">{emptyText}</div>
      ) : (
        filtered.map((o, i) => {
          const isSelected = selectedValues.includes(o.value);
          return (
            <div
              key={o.value}
              id={`${id}-opt-${i}`}
              role="option"
              aria-selected={isSelected}
              aria-disabled={o.disabled || undefined}
              data-index={i}
              data-active={i === active || undefined}
              className="rc-combobox__option"
              // onMouseDown, not onClick: mousedown fires before the input's
              // blur, so the selection lands before anything can close the
              // panel out from under the pointer.
              onMouseDown={(e) => { e.preventDefault(); commit(o); }}
              onMouseEnter={() => setActive(i)}
            >
              <span className="rc-combobox__check" aria-hidden>
                {isSelected ? <Check size={13} /> : null}
              </span>
              <span className="rc-combobox__option-text">
                <span className="rc-combobox__option-label">{o.label}</span>
                {o.hint ? <span className="rc-combobox__option-hint">{o.hint}</span> : null}
              </span>
            </div>
          );
        })
      )}
      {options.length > filtered.length && query === "" ? (
        <div className="rc-combobox__more">
          Showing {filtered.length} of {options.length} — type to narrow
        </div>
      ) : null}
    </div>,
    document.body
  ) : null;

  return (
    <div className={cn("rc-combobox", `rc-combobox--${size}`, error && "rc-combobox--error", disabled && "rc-combobox--disabled", className)}>
      <label htmlFor={id} className={cn("rc-combobox__label", hideLabel && "sr-only")}>
        {label}
        {required ? <span className="rc-combobox__required" aria-hidden="true">*</span> : null}
      </label>

      {/* Presentational shell, NOT a button — that is the whole fix for
          MultiSelect's nested-control bug. Clicking anywhere in it focuses
          the input; the chips' own remove buttons are ordinary siblings. */}
      <div
        ref={shellRef}
        className="rc-combobox__shell"
        onMouseDown={(e) => {
          if ((e.target as HTMLElement).closest(".rc-combobox__chip-remove")) return;
          e.preventDefault();
          inputRef.current?.focus();
          if (!disabled) setOpen(true);
        }}
      >
        {multiple
          ? selectedValues.map((v) => (
              <span key={v} className="rc-combobox__chip">
                {byValue.get(v)?.label ?? v}
                <button
                  type="button"
                  className="rc-combobox__chip-remove"
                  aria-label={`Remove ${byValue.get(v)?.label ?? v}`}
                  disabled={disabled}
                  onClick={() => removeValue(v)}
                >
                  <X size={11} aria-hidden />
                </button>
              </span>
            ))
          : null}

        <input
          ref={setInputRef}
          id={id}
          type="text"
          role="combobox"
          aria-expanded={open}
          aria-controls={open ? listboxId : undefined}
          aria-activedescendant={open && filtered[active] ? `${id}-opt-${active}` : undefined}
          aria-autocomplete="list"
          aria-invalid={error ? true : undefined}
          aria-describedby={[helpId, errorId].filter(Boolean).join(" ") || undefined}
          aria-required={required || undefined}
          autoComplete="off"
          disabled={disabled}
          className="rc-combobox__input"
          // In single-select the chosen label shows as the field's value
          // until the user types; in multi it is carried by the chips, so
          // the input is only ever the query.
          value={query !== "" || open || multiple ? query : singleSelected?.label ?? ""}
          placeholder={
            multiple
              ? selectedValues.length === 0 ? placeholder : ""
              : singleSelected ? singleSelected.label : placeholder
          }
          onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onKeyDown={onKeyDown}
        />

        {!multiple && props.value ? (
          <button
            type="button"
            className="rc-combobox__clear"
            aria-label="Clear selection"
            disabled={disabled}
            onClick={() => { removeValue(props.value as string); inputRef.current?.focus(); }}
          >
            <X size={13} aria-hidden />
          </button>
        ) : null}
        <span className="rc-combobox__chevron" aria-hidden>
          <ChevronDown size={15} />
        </span>
      </div>

      {help ? <span id={helpId} className="rc-combobox__help">{help}</span> : null}
      {error ? <span id={errorId} className="rc-combobox__error" role="alert">{error}</span> : null}
      {panel}
    </div>
  );
});
